/**
 * TermsConditionsModal
 * Displays Terms and Conditions for user acceptance
 * Web: Centered modal with backdrop
 * Native: Full-screen blocking view
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  NativeSyntheticEvent,
  NativeScrollEvent,
  LayoutChangeEvent,
  Dimensions,
} from 'react-native';
import { useTermsConditions } from '../contexts/TermsConditionsContext';
import { CheckCircle, ChevronDown } from './icons';
import DeviceInfo from 'react-native-device-info';

type Flavor = 'DEV' | 'QA' | 'PROD';
const FLAVOR_COLORS: Record<Flavor, { bg: string; label: string }> = {
  DEV: { bg: '#FF9500', label: 'DEVELOPMENT BUILD' },
  QA: { bg: '#007AFF', label: 'QA TESTING BUILD' },
  PROD: { bg: '#E31837', label: '' },
};

const isWeb = Platform.OS === 'web';
const { height: screenHeight } = Dimensions.get('window');

interface TermsConditionsModalProps {
  onAccepted?: () => void;
}

export const TermsConditionsModal: React.FC<TermsConditionsModalProps> = ({ onAccepted }) => {
  const {
    currentTC,
    isLoading,
    isAccepting,
    acceptTerms,
    error,
  } = useTermsConditions();

  const [flavor, setFlavor] = useState<Flavor>('PROD');
  
  useEffect(() => {
    const detectFlavor = async () => {
      if (Platform.OS !== 'web') {
        try {
          const appName = await DeviceInfo.getApplicationName();
          if (appName.toLowerCase().includes('dev')) {
            setFlavor('DEV');
          } else if (appName.toLowerCase().includes('qa')) {
            setFlavor('QA');
          }
        } catch (err) {
          console.log('Flavor detection error:', err);
        }
      }
    };
    detectFlavor();
  }, []);

  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const scrollViewRef = useRef<ScrollView>(null);

  // Track dimensions for scroll detection
  const [scrollViewHeight, setScrollViewHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollOffset, setScrollOffset] = useState(0);

  // Check if scrolled to bottom whenever dimensions or scroll position change
  const checkIfAtBottom = useCallback((
    viewHeight: number,
    totalContentHeight: number,
    offset: number
  ) => {
    if (viewHeight <= 0 || totalContentHeight <= 0) return;

    // If content fits in view without scrolling, enable accept immediately
    if (totalContentHeight <= viewHeight + 20) {
      if (!hasScrolledToBottom) {
        setHasScrolledToBottom(true);
        setShowScrollIndicator(false);
      }
      return;
    }

    // Consider scrolled to bottom when within 100px of the end (more forgiving threshold)
    const isAtBottom = offset + viewHeight >= totalContentHeight - 100;

    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
      setShowScrollIndicator(false);
    }

    // Hide scroll indicator when user has scrolled past the first screen
    if (offset > 100) {
      setShowScrollIndicator(false);
    }
  }, [hasScrolledToBottom]);

  // Effect to check scroll position when dimensions are known
  useEffect(() => {
    checkIfAtBottom(scrollViewHeight, contentHeight, scrollOffset);
  }, [scrollViewHeight, contentHeight, scrollOffset, checkIfAtBottom]);

  const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;

    // Update tracked values
    setScrollOffset(contentOffset.y);
    if (layoutMeasurement.height > 0) setScrollViewHeight(layoutMeasurement.height);
    if (contentSize.height > 0) setContentHeight(contentSize.height);

    // Direct check for immediate feedback
    checkIfAtBottom(layoutMeasurement.height, contentSize.height, contentOffset.y);
  }, [checkIfAtBottom]);

  const handleScrollViewLayout = useCallback((event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    setScrollViewHeight(height);
  }, []);

  const handleContentSizeChange = useCallback((width: number, height: number) => {
    setContentHeight(height);
  }, []);

  const handleAccept = useCallback(async () => {
    try {
      await acceptTerms();
      onAccepted?.();
    } catch (err) {
      // Error is handled in context
      console.error('Failed to accept T&C:', err);
    }
  }, [acceptTerms, onAccepted]);

  const scrollToBottom = useCallback(() => {
    scrollViewRef.current?.scrollToEnd({ animated: true });
  }, []);

  // Render HTML content (simplified - for production, consider using react-native-render-html)
  const renderContent = (htmlContent: string) => {
    // Simple HTML to text conversion for basic rendering
    // Remove HTML tags but preserve line breaks
    const textContent = htmlContent
      .replace(/<h2>/gi, '\n\n')
      .replace(/<\/h2>/gi, '\n')
      .replace(/<h3>/gi, '\n\n')
      .replace(/<\/h3>/gi, '\n')
      .replace(/<p>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<li>/gi, '\n  - ')
      .replace(/<\/li>/gi, '')
      .replace(/<ul>/gi, '')
      .replace(/<\/ul>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<strong>/gi, '')
      .replace(/<\/strong>/gi, '')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    return textContent;
  };

  // Loading state
  if (isLoading) {
    if (isWeb) {
      return (
        <View style={styles.webOverlay}>
          <View style={styles.webModal}>
            <View style={styles.webLoadingContainer}>
              <ActivityIndicator size="large" color="#FF6B35" />
              <Text style={styles.loadingText}>Loading Terms and Conditions...</Text>
            </View>
          </View>
        </View>
      );
    }
    return (
      <View style={styles.nativeContainer}>
        <View style={styles.nativeLoadingContainer}>
          <ActivityIndicator size="large" color="#FF6B35" />
          <Text style={styles.loadingText}>Loading Terms and Conditions...</Text>
        </View>
      </View>
    );
  }

  // Error state
  if (!currentTC) {
    // Determine error message - show offline-specific message if applicable
    const isOfflineError = error?.includes('offline') || error?.includes('connect to the internet');
    const errorMessage = isOfflineError
      ? 'Terms and Conditions not available offline'
      : 'Unable to load Terms and Conditions';
    const errorSubMessage = isOfflineError
      ? 'Please connect to the internet and try again.'
      : 'Please try again later';

    if (isWeb) {
      return (
        <View style={styles.webOverlay}>
          <View style={styles.webModal}>
            <View style={styles.webLoadingContainer}>
              <Text style={styles.errorText}>{errorMessage}</Text>
              <Text style={styles.errorSubtext}>{errorSubMessage}</Text>
            </View>
          </View>
        </View>
      );
    }
    return (
      <View style={styles.nativeContainer}>
        <View style={styles.nativeLoadingContainer}>
          <Text style={styles.errorText}>{errorMessage}</Text>
          <Text style={styles.errorSubtext}>{errorSubMessage}</Text>
        </View>
      </View>
    );
  }

  // Flavor banner component
  const flavorConfig = FLAVOR_COLORS[flavor];
  const FlavorBar = flavor !== 'PROD' ? (
    <View style={[styles.flavorBar, { backgroundColor: flavorConfig.bg }]}>
      <Text style={styles.flavorBarText}>{flavorConfig.label}</Text>
    </View>
  ) : null;

  // Main content - shared between web and native
  const modalContent = (
    <>
      {/* Flavor indicator bar */}
      {FlavorBar}
      {/* Header */}
      <View style={isWeb ? styles.webHeader : styles.nativeHeader}>
        <Text testID="tc-header-title" style={styles.headerTitle}>{currentTC.title}</Text>
        <Text style={styles.headerSubtitle}>Version {currentTC.version}</Text>
      </View>

      {/* Content */}
      <View style={styles.contentContainer}>
        <ScrollView
          ref={scrollViewRef}
          testID="tc-scroll-container"
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          onScroll={handleScroll}
          onLayout={handleScrollViewLayout}
          onContentSizeChange={handleContentSizeChange}
          onMomentumScrollEnd={handleScroll}
          scrollEventThrottle={16}
          showsVerticalScrollIndicator={true}
        >
          <Text style={styles.tcContent}>
            {renderContent(currentTC.content)}
          </Text>
        </ScrollView>

        {/* Scroll indicator */}
        {showScrollIndicator && (
          <TouchableOpacity
            testID="tc-scroll-indicator"
            style={styles.scrollIndicator}
            onPress={scrollToBottom}
            activeOpacity={0.7}
          >
            <Text style={styles.scrollIndicatorText}>Scroll to read all terms</Text>
            <ChevronDown size={20} color="#FF6B35" />
          </TouchableOpacity>
        )}
      </View>

      {/* Error message */}
      {error && (
        <View style={styles.errorBanner}>
          <Text style={styles.errorBannerText}>{error}</Text>
        </View>
      )}

      {/* Footer with Accept button */}
      <View style={isWeb ? styles.webFooter : styles.nativeFooter}>
        <Text style={styles.footerText}>
          {hasScrolledToBottom
            ? 'By clicking Accept, you agree to the Terms and Conditions above.'
            : 'Please scroll to the bottom to read all terms before accepting.'}
        </Text>
        <TouchableOpacity
          testID="tc-accept-button"
          style={[
            styles.acceptButton,
            !hasScrolledToBottom && styles.acceptButtonDisabled,
          ]}
          onPress={handleAccept}
          disabled={!hasScrolledToBottom || isAccepting}
          activeOpacity={0.7}
        >
          {isAccepting ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <>
              <CheckCircle size={20} color="#fff" />
              <Text style={styles.acceptButtonText}>Accept Terms & Conditions</Text>
            </>
          )}
        </TouchableOpacity>
      </View>
    </>
  );

  // Web: Modal with backdrop
  if (isWeb) {
    return (
      <View style={styles.webOverlay} testID="tc-modal-overlay">
        <View style={styles.webModal} testID="tc-modal">
          {modalContent}
        </View>
      </View>
    );
  }

  // Native: Full-screen
  return (
    <View style={styles.nativeContainer}>
      {modalContent}
    </View>
  );
};

const styles = StyleSheet.create({
  // ============ FLAVOR BAR ============
  flavorBar: {
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  flavorBarText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 2,
  },
  
  // ============ WEB STYLES ============
  webOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    ...(isWeb && {
      position: 'fixed' as any,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1000,
    }),
  },
  webModal: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    width: '100%',
    maxWidth: 700,
    maxHeight: '90%',
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    ...(isWeb && {
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)' as any,
    }),
  },
  webHeader: {
    paddingVertical: 20,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  webFooter: {
    backgroundColor: '#fff',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
    alignItems: 'center',
  },
  webLoadingContainer: {
    padding: 60,
    justifyContent: 'center',
    alignItems: 'center',
  },

  // ============ NATIVE STYLES ============
  nativeContainer: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  nativeLoadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  nativeHeader: {
    backgroundColor: '#fff',
    paddingTop: Platform.OS === 'ios' ? 60 : 40,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
    alignItems: 'center',
  },
  nativeFooter: {
    backgroundColor: '#fff',
    padding: 20,
    paddingBottom: Platform.OS === 'ios' ? 40 : 20,
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
    alignItems: 'center',
  },

  // ============ SHARED STYLES ============
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  errorText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#dc3545',
    marginBottom: 8,
  },
  errorSubtext: {
    fontSize: 14,
    color: '#666',
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#1C3A70',
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#666',
  },
  contentContainer: {
    flex: 1,
    position: 'relative',
    minHeight: isWeb ? 300 : undefined,
    maxHeight: isWeb ? screenHeight * 0.5 : undefined,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 40,
  },
  tcContent: {
    fontSize: 15,
    lineHeight: 24,
    color: '#333',
    ...(isWeb && {
      whiteSpace: 'pre-wrap' as any,
    }),
  },
  scrollIndicator: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    paddingVertical: 12,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
    gap: 8,
  },
  scrollIndicatorText: {
    fontSize: 14,
    color: '#FF6B35',
    fontWeight: '500',
  },
  errorBanner: {
    backgroundColor: '#f8d7da',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderTopWidth: 1,
    borderTopColor: '#f5c6cb',
  },
  errorBannerText: {
    fontSize: 14,
    color: '#721c24',
    textAlign: 'center',
  },
  footerText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 20,
  },
  acceptButton: {
    backgroundColor: '#FF6B35',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    minWidth: 250,
    ...(isWeb && {
      cursor: 'pointer' as any,
    }),
  },
  acceptButtonDisabled: {
    backgroundColor: '#ccc',
    ...(isWeb && {
      cursor: 'not-allowed' as any,
    }),
  },
  acceptButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default TermsConditionsModal;
